"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { DEFAULT_UI_LOCALE, uiDir, type UiLocale } from "@/lib/ui-i18n/config";
import { createFormatters, createTranslator, type Formatters, type RichValues, type Translator } from "@/lib/ui-i18n/core";
import type { AnyMessageKey, MessageKey, Messages, Namespace } from "@/lib/ui-i18n/dictionaries/types";
// Only the small, always-needed English namespaces are bundled as a fallback (used when a
// component renders outside any provider, e.g. on a tenant host). Never import the full index.
import enUi from "@/lib/ui-i18n/dictionaries/en/ui";
import enCommon from "@/lib/ui-i18n/dictionaries/en/common";

type Ctx = { locale: UiLocale; messages: Partial<Messages> };
const FALLBACK: Ctx = { locale: DEFAULT_UI_LOCALE, messages: { ui: enUi, common: enCommon } };
const UiI18nContext = createContext<Ctx | null>(null);

/**
 * Supplies the UI language and the given namespaces to client components. Nested providers
 * (same locale) MERGE their namespaces with the parent's, so a route can add its own:
 *   <UiI18nProvider locale={locale} messages={pickMessages(locale, ["content"])}>
 * Server components should use <UiMessages ns={[...]}> (components/i18n-ui/messages.tsx).
 */
export function UiI18nProvider({ locale, messages, children }: { locale: UiLocale; messages: Partial<Messages>; children: ReactNode }) {
  const parent = useContext(UiI18nContext);
  const value = useMemo<Ctx>(
    () => ({ locale, messages: parent && parent.locale === locale ? { ...parent.messages, ...messages } : messages }),
    [locale, messages, parent],
  );
  return <UiI18nContext.Provider value={value}>{children}</UiI18nContext.Provider>;
}

function useCtx(): Ctx {
  return useContext(UiI18nContext) ?? FALLBACK;
}

export function useUiLocale(): UiLocale {
  return useCtx().locale;
}

export function useUiDir(): "ltr" | "rtl" {
  return uiDir(useCtx().locale);
}

/**
 * Client translator. The namespace's messages must have been provided by an ancestor
 * (<UiMessages ns={["content"]}>); a missing key renders the key and warns in development.
 *   const t = useT("shell");  t("nav.dashboard");  t("header.notificationsUnread", { count: 3 })
 */
export function useT<N extends Namespace | undefined = undefined>(namespace?: N): Translator<N extends Namespace ? MessageKey<N> : AnyMessageKey> {
  const { locale, messages } = useCtx();
  return useMemo(() => createTranslator(locale, messages, namespace) as never, [locale, messages, namespace]);
}

/** Locale-aware Intl formatters. Dates default to UTC — see the hydration rule in core.ts. */
export function useFormat(): Formatters {
  const { locale } = useCtx();
  return useMemo(() => createFormatters(locale), [locale]);
}

/**
 * Inline translation that works inside isomorphic (server-or-client) components such as
 * src/components/ui/page.tsx:  <T k="ui.pagination.next" />  or with values / rich tags.
 * `fallback` is rendered when the key does not exist (data-driven keys like statuses).
 */
export function T({ k, values, fallback }: { k: AnyMessageKey | (string & {}); values?: RichValues; fallback?: ReactNode }) {
  const t = useT() as Translator<string>;
  if (fallback !== undefined && !t.has(k)) return <>{fallback}</>;
  return <>{t.rich(k, values)}</>;
}
